import { useEffect } from 'react';

/**
 * Reveals `.animate-on-scroll` elements (opacity 0 in CSS) by adding
 * `.visible` when they enter the viewport.
 *
 * IMPORTANT: elements are observed continuously, not only at mount. Several
 * homepage sections are lazy-loaded chunks that mount AFTER this hook's
 * effect runs; a one-time querySelectorAll would never observe them and they
 * would stay invisible forever (exactly the "page is empty below the fold"
 * bug). A MutationObserver picks up late-mounted elements as they appear.
 */
export function useInViewAnimation() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const observeWithin = (root: ParentNode) => {
      root.querySelectorAll('.animate-on-scroll:not(.visible)').forEach((el) => io.observe(el));
    };
    observeWithin(document);

    const mo = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.classList.contains('animate-on-scroll') && !node.classList.contains('visible')) {
            io.observe(node);
          }
          observeWithin(node);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, []);
}
